import useAlert from "@/components/common/modals/alert/context";
import api from "@/config/api";
import { Report } from "@/types/report";
import { i18n } from "@/utils/i18n";
import { log } from "@/utils/logger";
import storage from "@/utils/storage";
import { Box, CircularProgress, Switch } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";

interface Props {
    params: GridRenderCellParams;
}

const RegisterColumn: React.FC<Props> = ({ params: { row } }) => {
    const oid = storage.getItem("oid");
    const rid = row.id;
    const sid = row.sid;

    const queryClient = useQueryClient();
    const open = useAlert(state => state.open);
    const [loading, setLoading] = useState(false);
    const [registered, setRegistered] = useState<boolean>(row.registered);

    const onSwitch = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        e.preventDefault();

        const title = checked
            ? i18n.t("alert.reportColumn.register.title")
            : i18n.t("alert.reportColumn.unregister.title");

        const callback = async () => {
            try {
                setLoading(true);
                await api.patch(
                    `/organizations/${oid}/submissions/${sid}/reports/${rid}/registered`,
                    { data: { registered: checked } },
                );
                queryClient.setQueryData<Report[]>(["reports", oid], reports => {
                    return [
                        ...(reports ?? []).map(i => {
                            if (i.id === rid) return { ...i, registered: checked };
                            return i;
                        }),
                    ];
                });
                queryClient.invalidateQueries({ queryKey: ["reports", oid], exact: true });
                setRegistered(checked);
            } catch (e) {
                log.error(e);
            } finally {
                setLoading(false);
            }
        };

        open({ title, actions: { confirm: { callback } } });
    };

    return (
        <Box display="flex" alignItems="center">
            <Switch checked={registered} onChange={onSwitch} disabled={loading} />
            {loading && <CircularProgress size={18} />}
        </Box>
    );
};

export default RegisterColumn;
